'use client';

import { useId, useRef, useState, type FormEvent } from 'react';
import { Icon } from './Icon';
import type { Action } from '@to/public-vbc';

/** Data shape for inquiry form submissions. */
export interface InquiryFormData {
  intent?: 'offer_activation';
  operationId?: string;
  offerId?: string;
  senderName: string;
  senderEmail: string;
  senderPhone?: string;
  subject?: string;
  message: string;
}

export interface VBCInquiryFormProps {
  intent?: 'offer_activation';
  offerTitle?: string;
  selectedOffer?: string | null;
  onBehavior?: (action: Action) => void;
  vendorId: string | number;
  vendorName: string;
  /** Called when the form is submitted. App handles the API call and returns success/error. */
  onSubmitInquiry?: (
    vendorId: string | number,
    data: InquiryFormData,
  ) => Promise<{ success: boolean; error?: string }>;
}

/**
 * Inquiry form for the VBC detail page.
 * Form validation is handled locally; submission is delegated to the app via callback.
 */
export function VBCInquiryForm({
  vendorId,
  vendorName,
  selectedOffer,
  intent,
  offerTitle,
  onBehavior,
  onSubmitInquiry,
}: VBCInquiryFormProps) {
  const fieldId = useId();
  const activation = intent === 'offer_activation';
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const operation = useRef<{id:string;signature:string} | null>(null);
  const started = useRef(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'submitting' || !onSubmitInquiry || !e.currentTarget.reportValidity()) return;

    setStatus('submitting');
    setErrorMessage('');

    const formData = new FormData(e.currentTarget);
    const data: InquiryFormData = {
      ...(activation ? {intent:'offer_activation' as const} : {}),
      ...(selectedOffer ? { offerId: selectedOffer } : {}),
      senderName: formData.get('senderName') as string,
      senderEmail: (formData.get('senderEmail') as string).trim(),
      senderPhone: (formData.get('senderPhone') as string) || undefined,
      subject: (formData.get('subject') as string) || undefined,
      message: formData.get('message') as string,
    };

    const signature=JSON.stringify(data);
    if(operation.current?.signature!==signature)operation.current={id:crypto.randomUUID(),signature};
    data.operationId=operation.current.id;

    try {
      const result = await onSubmitInquiry(vendorId, data);

      if (result.success) {
        setStatus('success');
      } else {
        onBehavior?.({type:'inquiry_failed',placement:'response'});
        setStatus('error');
        setErrorMessage(result.error || 'Failed to send inquiry. Please try again.');
      }
    } catch {
      onBehavior?.({type:'inquiry_failed',placement:'transport'});
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  }

  if (status === 'success') {
    return (
      <div
        className="pv-inquiry-success" role="status"
        style={{
          borderColor: 'var(--brand-card-border)',
          backgroundColor: 'white',
        }}
      >
        <Icon
          name="check_circle"
          size={48}
          className="mx-auto mb-4"
          style={{ color: 'var(--color-success)' }}
        />
        <h3
          className="mb-2 text-lg font-semibold"
          style={{ color: 'var(--brand-text)' }}
        >
          {activation ? 'Offer activation sent' : 'Inquiry received'}
        </h3>
        <p
          className="text-sm"
          style={{ color: 'var(--brand-text-muted)' }}
        >
          {activation ? `Your offer activation request has been sent. ${vendorName} will reach out to you with next steps.` : `Your inquiry for ${vendorName} has been received. Email delivery is a separate step.`}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      onFocusCapture={() => { if(!started.current){started.current=true;onBehavior?.({type:'inquiry_started'});} }}
      onInvalidCapture={() => onBehavior?.({type:'inquiry_validation',placement:'required_field'})}
      className="pv-inquiry-form"
    >
      <div className="pv-form-field">
        <label htmlFor={`${fieldId}-senderName`}>Name <span className="pv-required">*</span></label>
        <input id={`${fieldId}-senderName`} name="senderName" autoComplete="name" required maxLength={200} placeholder="Your full name" />
      </div>
      <div className="pv-form-row">
        <div className="pv-form-field">
          <label htmlFor={`${fieldId}-senderEmail`}>Email <span className="pv-required">*</span></label>
          <input id={`${fieldId}-senderEmail`} name="senderEmail" type="email" required autoComplete="email" maxLength={255} placeholder="you@email.com" />
        </div>
        <div className="pv-form-field">
          <label htmlFor={`${fieldId}-senderPhone`}>Phone <span className="pv-optional">(optional)</span></label>
          <input id={`${fieldId}-senderPhone`} name="senderPhone" type="tel" autoComplete="tel" maxLength={30} placeholder="(555) 000-0000" />
        </div>
      </div>
      <div className="pv-form-field">
        <label htmlFor={`${fieldId}-message`}>Message <span className="pv-required">*</span></label>
        <textarea id={`${fieldId}-message`} name="message" defaultValue={activation ? `I would like to activate the offer: ${offerTitle ?? ''}.` : undefined} required maxLength={5000} rows={3} placeholder="Tell them how they can help…" />
      </div>
      {status === 'error' && errorMessage && <p className="pv-form-error" role="alert">{errorMessage}</p>}
      <button type="submit" disabled={status === 'submitting'} className="pv-primary-button">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={status === 'submitting' ? 'animate-spin' : undefined}><path d={status === 'submitting' ? 'M21 12a9 9 0 1 1-9-9' : 'M22 2 9 15M22 2l-7 20-6-7-7-6z'} /></svg>
        {status === 'submitting' ? 'Sending...' : activation ? 'Activate Offer' : 'Send Inquiry'}
      </button>
    </form>
  );
}
